import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { map, mergeMap, Observable, of, take } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  RouteSummary,
  RouteSummaryResponse,
} from 'src/app/core/models/operations';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { SummaryService } from 'src/app/core/services/operations/summary/summary.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { ServiceSupportUtils } from 'src/app/core/utils/service-support/service-support.utils';
import { FileUploadComponent } from '../shared/file-upload/file-upload.component';
import { CONSTS } from './route-summary.constants';
import { DataService } from 'src/app/core/services/data/data.service';

@Component({
  selector: 'app-route-summary',
  templateUrl: './route-summary.component.html',
  styleUrls: ['./route-summary.component.scss'],
  standalone: false,
})
export class RouteSummaryComponent implements OnInit {
  destroy = inject(DestroyRef);
  private locationService = inject(LocationService);
  private userService = inject(UserService);
  private prevLocation: string | null = null;
  private dateChangeDueToLocation = false;
  private hasHandledFirstLocation = false;
  private static lastKnownLocation: string | null = null;
  private readonly dataService = inject(DataService);

  readonly CONSTS = CONSTS;
  date: Date = new Date();

  minDate: Date = new Date(
    this.date.getFullYear(),
    this.date.getMonth(),
    this.date.getDate() - 30,
  );

  maxDate: Date = new Date(
    this.date.getFullYear(),
    this.date.getMonth(),
    this.date.getDate() + 20,
  );

  selectedDate = '';
  fileUploadAllowed: boolean = true;
  @Output()
  exportExcelEvent = new EventEmitter<boolean>();

  @Output()
  addRouteEvent = new EventEmitter<RouteSummary>();

  summaryData$!: Observable<RouteSummaryResponse>;
  routes: string[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly summaryService: SummaryService,
    private readonly serviceSupportUtils: ServiceSupportUtils,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  private loadRouteSummary(location: string, date: string): void {
    this.summaryService
      .getRouteSummary(location, date)
      .pipe(take(1))
      .subscribe((data) => {
        this.summaryData$ = of(data);
        this.routes = data.response.routesummary.map((d) => d.route);
      });
  }

  private handleLocationChange(): void {
    this.userService
      .getCurrentUser$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((user) => {
        if (!user?.location) return;

        const previousLocation = RouteSummaryComponent.lastKnownLocation;
        const locationChanged =
          previousLocation !== null && previousLocation !== user.location;

        RouteSummaryComponent.lastKnownLocation = user.location;

        if (!locationChanged) {
          return;
        }

        this.dateChangeDueToLocation = true;
        this.date = new Date();
        this.selectedDate = this.serviceSupportUtils.getMonthFirstFormattedDate(
          this.date,
        );

        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { date: this.selectedDate },
          queryParamsHandling: 'merge',
        });
        this.loadRouteSummary(user.location, this.selectedDate);
        this.dateChangeDueToLocation = false;
      });
  }

  /** Initializes component and loads route summary data, restoring date from query params if available */
  ngOnInit() {
    this.isFileUploadAllowed();

    const queryDate = this.activatedRoute.snapshot.queryParams['date'];
    if (queryDate) {
      this.date = new Date(queryDate);
      this.selectedDate = queryDate;
    } else {
      this.date = new Date();
      this.selectedDate = this.serviceSupportUtils.getMonthFirstFormattedDate(
        this.date,
      );
    }

    this.userService
      .getCurrentUser$()
      .pipe(take(1))
      .subscribe((user) => {
        if (user?.location && this.selectedDate) {
          this.loadRouteSummary(user.location, this.selectedDate);
        }
      });
    this.handleLocationChange();
  }

  /** Resets the date to current date */
  resetState() {
    this.date = new Date();
  }

  /** Checks if file upload is enabled for the current location */
  isFileUploadAllowed() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((location) => {
        this.fileUploadAllowed = location.location?.fileUploadEnabled ?? true;
      });
  }

  /** Emits event to trigger Excel export */
  sendExportExcelSignal() {
    this.exportExcelEvent.emit(true);
  }

  /** Emits event to add a new route */
  sendAddRouteSignal($event: RouteSummary) {
    this.addRouteEvent.emit($event);
  }

  /** Opens file upload dialog */
  fileUpload() {
    this.dialog.open(FileUploadComponent, {
      disableClose: true,
    });
  }

  /** Handles date changes and updates route summary data, persisting selection in query params */
  dateChanged(date: Date): void {
    if (this.dateChangeDueToLocation) return;

    const changedDate =
      this.serviceSupportUtils.getMonthFirstFormattedDate(date);

    if (!changedDate) return;

    this.selectedDate = changedDate;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { date: changedDate },
      queryParamsHandling: 'merge',
    });

    this.userService
      .getCurrentUser$()
      .pipe(take(1))
      .subscribe((user) => {
        if (user?.location) {
          this.loadRouteSummary(user.location, changedDate);
        }
      });
  }
}
